import {BoardBody, type BoardCardMenuPlacement, BoardProvider} from '@github-ui/board'
import {BoardColumnHeader, BoardRowHeader, BoardSkeleton, useAnnounceOnce} from '@github-ui/board/frames'
import {
  type BoardCardAddress,
  type BoardColumn,
  type BoardData,
  type BoardFocusPosition,
  type BoardItemDragState,
  type BoardItemsMove,
  type BoardLabels,
  type BoardPaging,
  type BoardReporting,
  type BoardRow,
  type CellAddress,
  useBoardFocus,
} from '@github-ui/board-core'
import {Blankslate} from '@primer/react/experimental'
import {Spinner, useMergedRefs} from '@primer/react'
import {type ReactNode, type Ref, useEffect} from 'react'

import styles from './BoardSurface.module.css'
import type {SavedViewsBoardData, SavedViewsBoardItem} from '../types'
import {useBoardViewportHeight} from './use-board-viewport-height'

/** A swimlane's maximum depth; shorter lanes fit their cards. */
const SWIMLANE_ROW_HEIGHT = '40rem'

/** A column's name and count, which is all a host with nothing richer to say needs. */
const renderPlainColumnHeader = (column: BoardColumn) => <BoardColumnHeader column={column} />

/** The same for a swimlane. */
const renderPlainRowHeader = (row: BoardRow) => <BoardRowHeader row={row} />

function BoardSurfaceFrame({
  children,
  testId,
  viewportAnchor,
  ref,
}: {
  children: ReactNode
  testId?: string
  viewportAnchor?: 'document' | 'viewport'
  ref?: Ref<HTMLDivElement>
}) {
  const measureRef = useBoardViewportHeight(viewportAnchor)
  const surfaceRef = useMergedRefs(measureRef, ref)

  return (
    <div ref={surfaceRef} className={styles.boardSurface} data-testid={testId}>
      {children}
    </div>
  )
}

export type BoardSurfaceSkeletonProps = {
  labels: BoardLabels
}

/**
 * A placeholder board in the box the board fills, for a page with no board on screen yet.
 *
 * The box is the board's own requirement rather than the placeholder's: the board owns
 * its scroll containers and takes its height from here, so a placeholder drawn outside
 * this box would move the page on the way in and again on the way out.
 */
export function BoardSurfaceSkeleton({labels}: BoardSurfaceSkeletonProps) {
  // Carries no `board-view` test id, which names the board being drawn. A placeholder
  // that answered to it would let a test waiting for a board settle on the thing
  // standing in for one.
  return (
    <BoardSurfaceFrame>
      <BoardSkeleton labels={labels} />
    </BoardSurfaceFrame>
  )
}

export type BoardFocusRequest = {
  position: BoardFocusPosition
  onHandled: () => void
}

export type BoardSurfaceProps<TItem extends SavedViewsBoardItem> = Pick<
  BoardReporting,
  'onDragChange' | 'onCardDialogChange'
> & {
  boardData: SavedViewsBoardData<TItem>
  renderCard: (item: TItem) => ReactNode
  /** The host's own items for one card's menu, shown above the move controls. See `Board`. */
  renderCardActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /** The host's own items for one card's menu, shown after the move controls. See `Board`. */
  renderCardTrailingActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /** See `Board`. */
  onCardMenuIntent?: (item: TItem) => void
  /**
   * Where the card menu button sits. See `Board`.
   * @default 'corner'
   */
  cardMenuPlacement?: BoardCardMenuPlacement
  /**
   * How a column names itself. Optional, because a column's name is the one thing this
   * layer can draw without knowing the domain, and a host that has richer material for
   * the field it grouped by -- a label's colour, a milestone's due date -- draws that
   * instead.
   */
  renderColumnHeader?: (column: BoardColumn) => ReactNode
  /**
   * The same for a swimlane. Ignored on a board with no swimlane, which has no row for a
   * reader to see: its single row is the board's own, not a bucket of a field.
   */
  renderRowHeader?: (row: BoardRow) => ReactNode
  /**
   * What is drawn instead of the grid when the response filled no column. Optional, because
   * why a grouped board came back empty is a statement about the search, which this layer is
   * handed nothing about. A host that supplies nothing gets the empty grid.
   */
  renderEmptyState?: () => ReactNode
  /** Optional: a host that supplies none still draws each cell's ghost, but nothing is asked. */
  onLoadMoreItems?: (address: CellAddress) => void
  onApproachItems?: BoardPaging['onApproachItems']
  onLoadMoreColumns?: BoardPaging['onLoadMoreColumns']
  onLoadMoreRows?: BoardPaging['onLoadMoreRows']
  onVisibleKeysChange?: BoardPaging['onVisibleKeysChange']
  /**
   * Whether the host has a request out that replaces this board. The board on screen stays
   * drawn but cannot be used, and a spinner shows over it, until the host passes new data.
   *
   * Stated by the host rather than read off the data, because what is in flight is the
   * host's request: a grouping change rebuilds both axes and a sort reorders every cell,
   * and neither is visible from the board data still on screen. The same fact
   * `IssuesListSurface` is handed for the list layout.
   */
  isLoading?: boolean
  labels: BoardLabels
  /**
   * Called when a drop moves a card somewhere it was not. **Supplying this is what makes the
   * board a drag surface**: leave it out and no card can be picked up and no cell is a drop
   * target, which is what a board whose viewer may move nothing should offer.
   *
   * The board writes nothing and holds no move of its own, so the card stays where it is
   * until `boardData` says otherwise.
   */
  onItemsMove?: (move: BoardItemsMove) => void
  /** Whether one card may be dragged, and why not when it may not. */
  getItemDragState?: (item: TItem) => BoardItemDragState
  /** Whether one card may land in one cell, from the cell it was dragged out of. */
  canDropItem?: (item: TItem, move: {from: CellAddress; to: CellAddress}) => boolean
  /**
   * Whether the cards in one cell are in an order somebody chose, which makes a place inside the
   * cell something a drop can aim at. Leave it out for a board its sort orders.
   */
  canReorderCards?: (cell: CellAddress) => boolean
  focusRequest?: BoardFocusRequest
  /** The box around the board, which holds the board's `<section>` when there is one. */
  ref?: Ref<HTMLDivElement>
  viewportAnchor?: 'document' | 'viewport'
  boardClassName?: string
}

/**
 * Nothing is coming for this axis: no column arrived, none is on its way, and the server
 * did not stop short of one.
 *
 * `hasMoreColumns` and `isLoadingColumns` are a board mid-enumeration, and `columnsTruncated`
 * is `BoardColumnPaging` drawing the notice to narrow the search -- each of them says
 * something about an axis that is not yet, or never will be, complete, and an empty state
 * drawn over any of them replaces a true statement with a wrong one.
 */
function hasNoColumns<TItem>(data: BoardData<TItem>): boolean {
  return (
    data.columns.length === 0 &&
    !data.hasMoreColumns &&
    data.isLoadingColumns !== true &&
    data.columnsTruncated !== true
  )
}

/**
 * The generic glue over `@github-ui/board`: it wires the mapped data and is agnostic about the
 * domain except for the `renderCard` and `renderColumnHeader` it is handed. A different host
 * reuses this unchanged and supplies its own board data, card and header.
 *
 * The board owns its own scroll containers and cannot give itself a height: it fills its parent
 * and windows against that parent's height, so an unbounded parent makes it stretch the page and
 * its scrolling, virtualization and paging break together and silently. The host is sized to the
 * viewport at runtime (see `useBoardViewportHeight`) because the page layout above it renders a
 * header and sidebar and so cannot hand the board a bounded height in CSS alone.
 */
export function BoardSurface<TItem extends SavedViewsBoardItem>({
  isLoading = false,
  ...props
}: BoardSurfaceProps<TItem>) {
  // The spinner's text is not a live region, and a busy board keeps focus where it was.
  useAnnounceOnce(props.labels.loading, isLoading)

  // Mounted whether or not a request is out, so marking the board busy keeps it mounted
  // with its scroll position and the pages it loaded.
  return (
    <div className={styles.surface} data-busy={isLoading || undefined}>
      <div className={styles.content} inert={isLoading || undefined}>
        <BoardSurfaceContent {...props} />
      </div>
      {isLoading && (
        <div className={styles.busyIndicator}>
          <Spinner delay="short" srText={props.labels.loading} />
        </div>
      )}
    </div>
  )
}

function BoardSurfaceContent<TItem extends SavedViewsBoardItem>({
  boardData,
  renderCard,
  renderCardActions,
  renderCardTrailingActions,
  onCardMenuIntent,
  cardMenuPlacement,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyState,
  onLoadMoreItems,
  onApproachItems,
  onLoadMoreColumns,
  onLoadMoreRows,
  onVisibleKeysChange,
  labels,
  onItemsMove,
  getItemDragState,
  canDropItem,
  canReorderCards,
  focusRequest,
  onDragChange,
  onCardDialogChange,
  viewportAnchor,
  boardClassName,
  ref,
}: Omit<BoardSurfaceProps<TItem>, 'isLoading'>) {
  const {data, columnField, rowField} = boardData
  const swimlaned = rowField != null

  // D29: a board with no grouping has nothing to arrange into columns. That is an empty
  // state, not a fabricated single column.
  if (columnField == null) {
    return (
      <Blankslate>
        <Blankslate.Heading>Nothing to group by</Blankslate.Heading>
        <Blankslate.Description>Choose a field to group by to see this view as a board.</Blankslate.Description>
      </Blankslate>
    )
  }

  if (renderEmptyState && hasNoColumns(data)) return renderEmptyState()

  return (
    <BoardSurfaceFrame ref={ref} testId="board-view" viewportAnchor={viewportAnchor}>
      <BoardProvider<TItem>
        data={data}
        getItemId={item => item.globalId}
        getItemLabel={item => item.title}
        labels={labels}
        hasRowHeaders={swimlaned}
        onLoadMoreItems={onLoadMoreItems}
        onApproachItems={onApproachItems}
        onLoadMoreColumns={onLoadMoreColumns}
        onLoadMoreRows={onLoadMoreRows}
        onVisibleKeysChange={onVisibleKeysChange}
        onItemsMove={onItemsMove}
        getItemDragState={getItemDragState}
        canDropItem={canDropItem}
        canReorderCards={canReorderCards}
        onDragChange={onDragChange}
        onCardDialogChange={onCardDialogChange}
      >
        <BoardFocusRestoration request={focusRequest} />
        <BoardBody
          renderCard={renderCard}
          renderCardActions={renderCardActions}
          renderCardTrailingActions={renderCardTrailingActions}
          onCardMenuIntent={onCardMenuIntent}
          cardMenuPlacement={cardMenuPlacement}
          renderColumnHeader={renderColumnHeader ?? renderPlainColumnHeader}
          renderRowHeader={swimlaned ? (renderRowHeader ?? renderPlainRowHeader) : undefined}
          rowHeight={swimlaned ? SWIMLANE_ROW_HEIGHT : undefined}
          className={boardClassName}
        />
      </BoardProvider>
    </BoardSurfaceFrame>
  )
}

function BoardFocusRestoration({request}: {request?: BoardFocusRequest}) {
  const {focusPosition} = useBoardFocus()

  // A passive effect runs after the dismissed dialog has released its focus trap.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the request must wait for dialog cleanup, not run in its close handler
    if (!request) return
    focusPosition(request.position)
    request.onHandled()
  }, [focusPosition, request])

  return null
}
